import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Alert,
  AlertTitle,
  Chip,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import Dropzone from '../../../components/Dropzone/Dropzone';
import QuerySelect from '../../../components/common/QuerySelect';
import {
  GetAgentOffices,
  OutstandingPolicyList,
  OutstandingPolicyListFast,
  SaveUploadedFiles,
  DeleteFile,
} from '../../../api/SecureFileUploader';
import { SecurelyGetProducerInformation } from '../../../api/admin';
import { GetStateList } from '../../../api/proposals';
import { useQuery, useMutation } from '@tanstack/react-query';
import { moveObjToTopOfArr } from '../../../components/Utilities/HelperMethods';
import LoadingIndicator from '../../../components/Global/loading-indicator/loading-indicator';

const MAX_FILE_SIZE_MB = 50;

const SIGNER_ROWS = [
  { index: 0, label: 'Insured', required: true, helper: null, disabled: false },
  {
    index: 1,
    label: 'Owner',
    required: false,
    helper: 'If different from Insured',
    disabled: false,
  },
  {
    index: 2,
    label: 'Annuitant',
    required: false,
    helper: 'If different from Insured',
    disabled: false,
  },
  { index: 3, label: 'Spouse', required: false, helper: null, disabled: false },
  { index: 4, label: 'Agent', required: true, helper: null, disabled: true },
];

/**
 * Gets required fields based on the current file type
 * @param {string} fileType - The current file type
 * @returns {string[]} - Array of required field paths
 */
const getRequiredFields = (fileType) => {
  switch (fileType) {
    case 'App':
      return ['fileType', 'underwriteType', 'agencyName', 'files'];
    case 'OutReq':
      return ['fileType', 'files']; // policy is optional
    case 'MygaOutReq':
      return ['fileType', 'producerFilter', 'policyId', 'files'];
    case 'MygaSuit':
      return ['fileType', 'policyId', 'files'];
    case 'eApp':
      return [
        'fileType',
        'files',
        'dataUploadRequest.0.fullName',
        'dataUploadRequest.0.emailAddress',
        'dataUploadRequest.4.fullName',
        'dataUploadRequest.4.emailAddress',
      ];
    case 'Work':
      return ['fileType', 'files'];
    default:
      return ['fileType', 'files'];
  }
};

/**
 * Validates that an email is not duplicated among other signers
 * @param {string} emailAddress - The email to validate
 * @param {number} currentIndex - The index of the current signer
 * @param {Function} getValues - React Hook Form's getValues function
 * @returns {boolean|string} - true if valid, error message if duplicate
 */
const validateUniqueEmail = (emailAddress, currentIndex, getValues) => {
  if (!emailAddress) return true;

  const dataUploadRequest = getValues('dataUploadRequest');
  const normalizedEmail = emailAddress.toLowerCase().trim();

  const duplicateIndex = dataUploadRequest.findIndex(
    (signer, index) =>
      index !== currentIndex && signer.emailAddress?.toLowerCase().trim() === normalizedEmail
  );

  if (duplicateIndex !== -1) {
    const duplicateLabel = SIGNER_ROWS[duplicateIndex]?.label || `Signer ${duplicateIndex + 1}`;
    return `Email already used for ${duplicateLabel}`;
  }

  return true;
};

const SignerRow = ({
  index,
  label,
  required,
  helper,
  disabled,
  control,
  states,
  getValues,
  trigger,
}) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={{ xs: 2, sm: 2 }}
    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
    sx={{ pb: 1, pt: 1 }}
  >
    <Box
      sx={{
        width: { xs: '100%', sm: 90 },
        pt: { xs: 0, sm: 1 },
        flexShrink: 0,
      }}
    >
      <Typography variant="body2">
        {label}:{required && <span style={{ color: 'red' }}>*</span>}
      </Typography>
    </Box>
    <Controller
      name={`dataUploadRequest.${index}.fullName`}
      control={control}
      rules={{ required: required ? 'Required' : false }}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          size="small"
          label="Full Name"
          sx={{ flex: { sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
          error={!!error}
          helperText={error?.message || helper}
          disabled={disabled}
        />
      )}
    />
    <Controller
      name={`dataUploadRequest.${index}.emailAddress`}
      control={control}
      rules={{
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email',
        },
        required: required ? 'Required' : false,
        validate: (value) => validateUniqueEmail(value, index, getValues),
      }}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          onChange={(e) => {
            field.onChange(e);
            SIGNER_ROWS.forEach((_, i) => {
              if (i !== index) trigger(`dataUploadRequest.${i}.emailAddress`);
            });
          }}
          size="small"
          label="Email Address"
          sx={{ flex: { sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
          error={!!error}
          helperText={error?.message}
          disabled={disabled}
        />
      )}
    />
    <FormControl sx={{ width: { xs: '100%', sm: 150 } }} size="small">
      <InputLabel>State</InputLabel>
      <Controller
        name={`dataUploadRequest.${index}.signatureState`}
        control={control}
        render={({ field }) => (
          <Select {...field} label="State">
            {states.map((state) => (
              <MenuItem key={state.Value} value={state.Value}>
                {state.Key}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FormControl>
  </Stack>
);

const SecureFileUploaderModalContent = ({
  onSubmit,
  setSubmitForm,
  permissions,
  producerID,
  agency,
  agentInfo,
  sessionGuid,
  handleCancel,
  setHasFiles,
  isDisabled,
  products,
  setFileOpsStatus,
  onFormValidChange,
}) => {
  const { hasMYGA, canUseEApp, isCASG } = permissions;
  const { watch, control, handleSubmit, setValue, getValues, trigger, resetField } = useForm({
    defaultValues: {
      fileType: '',
      underwriteType: '',
      files: [],
      overrideHome: false,
      policyId: '',
      producerFilter: producerID,
      producerId: producerID,
      npnId: agentInfo.npnID,
      guid: sessionGuid,
      agencyName: agency ? (agency === 'ALL' ? '' : agency) : '',
      dataUploadRequest: [
        {
          party: 'Insured',
          fullName: '',
          emailAddress: '',
          signatureState: 'AL',
        },
        {
          party: 'Owner',
          fullName: '',
          emailAddress: '',
          signatureState: 'AL',
        },
        {
          party: 'Annuitant',
          fullName: '',
          emailAddress: '',
          signatureState: 'AL',
        },
        {
          party: 'Spouse',
          fullName: '',
          emailAddress: '',
          signatureState: 'AL',
        },
        {
          party: 'Agent',
          fullName: agentInfo.displayName,
          emailAddress: agentInfo.emailAddress,
          signatureState: 'AL',
        },
      ],
    },
    mode: 'onChange',
  });

  const formValues = watch();

  const saveUploadedFilesMutation = useMutation({
    mutationFn: (files) => SaveUploadedFiles(files)(),
  });
  const deleteFileMutation = useMutation({
    mutationFn: (fileName) => DeleteFile({ fileName })(),
  });

  const { data: states, isLoading: statesLoading } = useQuery({
    queryKey: ['sfuGetStateList'],
    queryFn: GetStateList(),
    enabled: formValues.fileType === 'eApp',
  });

  useEffect(() => {
    setSubmitForm(() => handleSubmit(onSubmit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formValues.fileType !== 'App') {
      setValue('underwriteType', '');
    }
  }, [formValues.fileType, setValue]);

  useEffect(() => {
    const fileOpsInProgress = saveUploadedFilesMutation.isLoading || deleteFileMutation.isLoading;
    setFileOpsStatus?.(fileOpsInProgress);
  }, [saveUploadedFilesMutation.isLoading, deleteFileMutation.isLoading, setFileOpsStatus]);

  // Custom validation logic based on file type
  useEffect(() => {
    const requiredFields = getRequiredFields(formValues.fileType);

    const allFieldsValid = requiredFields.every((fieldPath) => {
      // Handle nested fields like 'signers.0.fullName'
      const value = fieldPath.split('.').reduce((obj, key) => obj?.[key], formValues);

      // Check if value exists and is not empty
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null && value !== undefined;
    });

    // For eApp, also check for email validation errors
    let hasEmailErrors = false;
    if (formValues.fileType === 'eApp') {
      const insuredEmail = formValues.dataUploadRequest?.[0]?.emailAddress;
      const agentEmail = formValues.dataUploadRequest?.[4]?.emailAddress;

      // Basic email validation
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      hasEmailErrors = !emailRegex.test(insuredEmail) || !emailRegex.test(agentEmail);

      // Check for duplicate emails
      if (!hasEmailErrors) {
        hasEmailErrors =
          validateUniqueEmail(insuredEmail, 0, getValues) !== true ||
          validateUniqueEmail(agentEmail, 4, getValues) !== true;
      }
    }

    const isFormValid = allFieldsValid && !hasEmailErrors;
    onFormValidChange?.(isFormValid);
  }, [formValues, onFormValidChange, getValues]);

  return (
    <Stack gap={1}>
      <Box sx={{ paddingBottom: '1rem' }}>
        <Chip variant="outlined" icon={<PersonIcon />} label={`${producerID}`} />
      </Box>
      <Stack direction={'row'} gap={2} paddingBottom={1}>
        <Controller
          name="fileType"
          control={control}
          rules={{ required: 'File Type is required' }}
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ flex: 1 }} fullWidth variant="outlined" error={!!error}>
              <InputLabel id="file-type-select-label">File Type</InputLabel>
              <Select
                {...field}
                labelId="file-type-select-label"
                label="File Type"
                disabled={isDisabled}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="App">Application</MenuItem>
                <MenuItem value="OutReq">Outstanding Requirements</MenuItem>
                {hasMYGA && <MenuItem value="MygaOutReq">IQumulate Outstanding Reqs.</MenuItem>}
                {hasMYGA && <MenuItem value="MygaSuit">IQumulate Suitability</MenuItem>}
                {isCASG && <MenuItem value="Work">Worksite</MenuItem>}
                {canUseEApp && <MenuItem value="eApp">eApp - GENL and SPWL only</MenuItem>}
              </Select>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      </Stack>
      {formValues.fileType === 'App' && (
        <Stack direction={'row'} gap={1}>
          <Controller
            name="underwriteType"
            control={control}
            rules={{ required: 'Product is required' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth variant="outlined" error={!!error}>
                <InputLabel id="select-label">Product</InputLabel>
                <Select
                  value={field.value || ''}
                  labelId="file-type-select-label"
                  label="Product"
                  disabled={isDisabled}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
          {permissions.isAdmin && (
            <Controller
              name="agencyName"
              control={control}
              rules={{ required: 'Agency is required' }}
              render={({ field, fieldState: { error } }) => (
                <QuerySelect
                  {...field}
                  queryKey={['GetAgentOfficesModal']}
                  queryFn={GetAgentOffices(
                    {
                      ProducerID: producerID,
                      IsRSA: permissions.isRSA,
                      IsManager: permissions.isManager,
                    },
                    {
                      retry: 1,
                      timeout: 200,
                    }
                  )}
                  queryOptions={{
                    select: (data) => {
                      return data.filter((agency) => agency.agency !== 'ALL');
                    },
                  }}
                  label="Upload to Agency"
                  value={formValues.agencyName}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('agency', e.target.value);
                  }}
                  getOptionLabel={(option) => option.agency}
                  getOptionValue={(option) => option.agency}
                  fallbackData={{
                    agency: agency,
                  }}
                  error={!!error}
                  helperText={error?.message}
                  disabled={isDisabled}
                />
              )}
            />
          )}
        </Stack>
      )}

      {formValues.fileType === 'OutReq' && (
        <Stack direction={'row'} gap={1}>
          <Controller
            name="policyId"
            control={control}
            render={({ field }) => (
              <QuerySelect
                {...field}
                queryKey={['getOutstandingPolicies', producerID, formValues.agencyName]}
                queryFn={() =>
                  OutstandingPolicyList({
                    ProducerId: producerID,
                    agency: agentInfo.agencyName,
                    IsRSA: permissions.isRSA,
                  })()
                }
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                label="Policy"
                value={formValues.policyId}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('policyId', e.target.value);
                }}
                emptyText="No outstanding requirements. You may still submit a file."
                disabled={isDisabled}
              />
            )}
          />
        </Stack>
      )}

      {formValues.fileType === 'MygaOutReq' && (
        <Stack direction={'row'} gap={1}>
          <Controller
            name="producerFilter"
            control={control}
            rules={{ required: 'Agent ID is required' }}
            render={({ field, fieldState: { error } }) => (
              <QuerySelect
                {...field}
                queryKey={['sfuGetProducerInformation', producerID]}
                queryFn={() =>
                  SecurelyGetProducerInformation({
                    QueryString: '',
                  })()
                }
                queryOptions={{
                  select: (data) => {
                    const res = moveObjToTopOfArr(
                      JSON.parse(data),
                      (producer) => producer.ProducerId.trim() === producerID
                    );
                    return res;
                  },
                }}
                label="Agent ID (and Downline)"
                getOptionLabel={(option) => option.ProducerId.trim()}
                getOptionValue={(option) => option.ProducerId.trim()}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('producerFilter', e.target.value);
                  resetField('policyId');
                }}
                value={formValues.producerFilter}
                defaultToFirst={true}
                error={!!error}
                helperText={error?.message}
                disabled={isDisabled}
              />
            )}
          />
          <Controller
            name="policyId"
            control={control}
            rules={{ required: 'Policy is required' }}
            render={({ field, fieldState: { error } }) => (
              <QuerySelect
                {...field}
                queryKey={['getOutstandingPolicyListFast', formValues.producerFilter]}
                queryFn={() =>
                  OutstandingPolicyListFast({
                    ProducerID: formValues.producerFilter,
                    IsRSA: formValues.producerFilter === producerID ? permissions.isRSA : false,
                  })()
                }
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                label="Policy"
                defaultToFirst={true}
                emptyText="No outstanding IQ policies found"
                value={formValues.policyId}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('policyId', e.target.value);
                }}
                disabled={isDisabled}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Stack>
      )}

      {formValues.fileType === 'MygaSuit' && (
        <Stack direction={'row'} gap={1}>
          <Controller
            name="policyId"
            control={control}
            rules={{ required: 'Policy is required' }}
            render={({ field, fieldState: { error } }) => (
              <QuerySelect
                {...field}
                queryKey={['getSuitabilityPolicies', producerID, formValues.agencyName]}
                queryFn={() =>
                  OutstandingPolicyListFast({
                    ProducerId: producerID,
                    IsRSA: permissions.isRSA,
                  })()
                }
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                label="Policy"
                emptyText="No Suitability policies found"
                value={formValues.policyId}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('policyId', e.target.value);
                }}
                error={!!error}
                helperText={error?.message}
                disabled={isDisabled}
              />
            )}
          />
        </Stack>
      )}

      {formValues.fileType === 'eApp' && statesLoading && (
        <Box padding={'1rem 0'}>
          <LoadingIndicator />
        </Box>
      )}

      {formValues.fileType === 'eApp' && states && agentInfo && (
        <Box sx={{ mt: 2, mb: 2 }}>
          {SIGNER_ROWS.map((row) => (
            <SignerRow
              key={row.index}
              index={row.index}
              label={row.label}
              required={row.required}
              helper={row.helper}
              disabled={row.disabled}
              control={control}
              states={states.states}
              getValues={getValues}
              trigger={trigger}
            />
          ))}
        </Box>
      )}
      <Alert severity="warning">
        <AlertTitle>File Size Limit</AlertTitle>
        Each uploaded file must be <strong>{MAX_FILE_SIZE_MB} MB or less</strong>. Files exceeding this limit will
        not be accepted.
      </Alert>
      <Stack>
        <Controller
          name="files"
          control={control}
          rules={{
            validate: (value) => (value && value.length > 0) || 'At least one file is required',
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              <Dropzone
                onFilesAccepted={(files) => {
                  field.onChange(files);
                  setHasFiles(files.length > 0);
                }}
                maxFiles={
                  formValues.fileType === 'App' && formValues.underwriteType === 4 ? 1 : 100
                }
                accept={{
                  'application/pdf': ['.pdf'],
                }}
                dropzoneText="Drag & drop files here, or click to select files"
                clearFiles={null}
                error={!!error}
                onUpload={(files) => {
                  const formData = new FormData();
                  const renamedFiles = files.map(
                    (file) =>
                      new File([file], `${formValues.guid}${file.name}`, {
                        type: file.type,
                      })
                  );
                  renamedFiles.forEach((file) => formData.append('files', file));
                  return saveUploadedFilesMutation.mutateAsync(formData);
                }}
                onCancel={handleCancel}
                isDeleting={deleteFileMutation.isLoading}
                onDelete={(file) => {
                  return deleteFileMutation.mutateAsync(`${formValues.guid}${file.name}`);
                }}
                disabled={isDisabled}
              />
              {error && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  {error.message}
                </Typography>
              )}
            </>
          )}
        />
      </Stack>
      {permissions.isCASG && formValues.fileType === 'App' && (
        <>
          <FormControl>
            <Controller
              name="overrideHome"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={isDisabled} />}
                  label="Send to Home Office"
                />
              )}
            />
          </FormControl>
          <Stack gap={1}>
            {formValues.underwriteType === 4 && (
              <Alert severity="warning">
                <AlertTitle>Warning:</AlertTitle>
                Only IQumulate policies should be processed in this section. All application files
                should be in one PDF.
              </Alert>
            )}
            {formValues.overrideHome && (
              <Alert severity="info">
                <AlertTitle>Notice:</AlertTitle>
                By Sending to Home Office you are acknowledging that a manager has approved the
                submitted document.
              </Alert>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default SecureFileUploaderModalContent;
